/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, X } from "lucide-react";
import { directoryApi } from "./api";
import "./ContactForm.css";

const EMPTY = {
  category_id: "",
  name: "",
  primary_phone: "",
  secondary_phone: "",
  whatsapp_phone: "",
  email: "",
  address: "",
  working_hours: "",
  services_offered: "",
  description: "",
};

export default function ContactForm({ mode = "create" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(null);
  const [showCatForm, setShowCatForm] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);

  // Load categories on mount, and contact when in edit mode.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await directoryApi.listCategories();
        if (cancelled) return;
        setCategories(cats);
        if (mode === "edit") {
          const c = await directoryApi.getContact(id);
          if (cancelled) return;
          setForm({
            category_id: c.category?.id || "",
            name: c.name || "",
            primary_phone: c.primary_phone || "",
            secondary_phone: c.secondary_phone || "",
            whatsapp_phone: c.whatsapp_phone || "",
            email: c.email || "",
            address: c.address || "",
            working_hours: c.working_hours || "",
            services_offered: c.services_offered || "",
            description: c.description || "",
          });
          setLoading(false);
        } else {
          // Default to the first non-Other seed if none set
          if (cats.length > 0 && !form.category_id) {
            setForm((f) => ({ ...f, category_id: cats[0].id }));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Could not load");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setConflict(null);
    if (!form.category_id) {
      setError("Pick a category");
      return;
    }
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!form.primary_phone.trim()) {
      setError("Primary phone is required");
      return;
    }
    setSubmitting(true);
    try {
      const body = { ...form };
      // Empty strings -> null for optional phones so backend validates
      ["secondary_phone", "whatsapp_phone", "email", "address",
       "working_hours", "services_offered", "description"].forEach((k) => {
        if (!body[k] || !body[k].trim()) body[k] = null;
      });
      const saved =
        mode === "edit"
          ? await directoryApi.patchContact(id, body)
          : await directoryApi.createContact(body);
      navigate(`/directory/contact/${saved.id}`);
    } catch (err) {
      const d = err.detail;
      if (d && typeof d === "object" && d.code === "phone_already_exists") {
        setConflict(d.existing || { name: "another contact" });
      } else {
        setError(err.message || "Could not save");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onCreateCategory = async (e) => {
    e?.preventDefault();
    if (!newCatName.trim()) return;
    setCreatingCat(true);
    try {
      const cat = await directoryApi.createCategory(newCatName.trim());
      setCategories((cs) =>
        cs.some((c) => c.id === cat.id) ? cs : [...cs, cat]
      );
      setForm((f) => ({ ...f, category_id: cat.id }));
      setNewCatName("");
      setShowCatForm(false);
    } catch (err) {
      setError(err.message || "Could not create category");
    } finally {
      setCreatingCat(false);
    }
  };

  if (loading)
    return (
      <div className="vsd-cf-shell">
        <div className="vsd-empty">Loading…</div>
      </div>
    );

  return (
    <div className="vsd-cf-shell">
      <Link to="/directory" className="vsd-back-link">
        <ArrowLeft size={14} /> Back to directory
      </Link>
      <h1 className="vsd-cf-h1">
        {mode === "edit" ? "Edit contact" : "Add a service contact"}
      </h1>
      <p className="vsd-cf-sub">
        {mode === "edit"
          ? "Update the details below. Changes go live instantly."
          : "Share a plumber, doctor, vendor, or anyone the community can hire."}
      </p>

      {conflict && (
        <div className="vsd-cf-conflict">
          <strong>Already in the directory</strong>
          <span>
            That phone number is registered as{" "}
            <Link to={`/directory/contact/${conflict.id}`}>
              {conflict.name}
            </Link>
            . You can leave a review there instead.
          </span>
        </div>
      )}

      <form className="vsd-cf-form" onSubmit={submit}>
        <Field
          label="Name"
          required
          value={form.name}
          onChange={(v) => update({ name: v })}
          placeholder="e.g. Ramesh Plumber"
        />

        <div className="vsd-cf-field">
          <label>
            Category <span className="vsd-cf-req">*</span>
          </label>
          {!showCatForm ? (
            <div className="vsd-cf-cat-row">
              <select
                value={form.category_id}
                onChange={(e) => update({ category_id: e.target.value })}
              >
                <option value="">— pick one —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="vsd-cf-cat-add"
                onClick={() => setShowCatForm(true)}
              >
                <Plus size={13} /> Add new
              </button>
            </div>
          ) : (
            <div className="vsd-cf-cat-create">
              <input
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder="e.g. Pet Care"
                maxLength={64}
                autoFocus
              />
              <button
                type="button"
                onClick={onCreateCategory}
                disabled={!newCatName.trim() || creatingCat}
                className="vsd-cf-cat-save"
              >
                {creatingCat ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="vsd-cf-cat-cancel"
                onClick={() => {
                  setShowCatForm(false);
                  setNewCatName("");
                }}
                aria-label="Cancel new category"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        <Field
          label="Primary phone"
          required
          value={form.primary_phone}
          onChange={(v) => update({ primary_phone: v })}
          placeholder="9876543210"
          inputMode="tel"
        />

        <div className="vsd-cf-grid">
          <Field
            label="Secondary phone (optional)"
            value={form.secondary_phone}
            onChange={(v) => update({ secondary_phone: v })}
            placeholder=""
            inputMode="tel"
          />
          <Field
            label="WhatsApp (if different)"
            value={form.whatsapp_phone}
            onChange={(v) => update({ whatsapp_phone: v })}
            placeholder=""
            inputMode="tel"
          />
        </div>

        <Field
          label="Email (optional)"
          type="email"
          value={form.email}
          onChange={(v) => update({ email: v })}
          placeholder=""
        />

        <Field
          label="Address / service area (optional)"
          value={form.address}
          onChange={(v) => update({ address: v })}
          placeholder="e.g. Sector 15, Tukkuguda"
        />

        <Field
          label="Working hours (optional)"
          value={form.working_hours}
          onChange={(v) => update({ working_hours: v })}
          placeholder="e.g. Mon–Sat 9 AM – 7 PM"
        />

        <Field
          label="Services offered (comma-separated)"
          value={form.services_offered}
          onChange={(v) => update({ services_offered: v })}
          placeholder="Tap repair, Pipe fitting, Bathroom renovation"
        />

        <div className="vsd-cf-field">
          <label>Description / why you recommend (optional)</label>
          <textarea
            rows={4}
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Share what you liked, the quality of work, pricing notes, etc."
            maxLength={2000}
          />
        </div>

        {error && <div className="vsd-cf-error">{error}</div>}

        <div className="vsd-cf-actions">
          <button
            type="button"
            className="vsd-btn-secondary"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="vsd-btn-primary"
            disabled={submitting}
          >
            {submitting
              ? "Saving…"
              : mode === "edit"
              ? "Save changes"
              : "Add contact"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, value, onChange, placeholder, type = "text", inputMode }) {
  return (
    <div className="vsd-cf-field">
      <label>
        {label}
        {required && <span className="vsd-cf-req"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
      />
    </div>
  );
}
